import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './homepage.css';

import {
  meta,
  approach,
  connector,
  problem,
  howItWorks,
  whatYouGet,
  bringYourOwn,
  pricing,
  faq,
  footer,
} from './content';
import { ClaudeMark } from './components/bits';
import PromoVideo from './components/PromoVideo';
import StorySection from './components/StorySection';
import JobMapSection from './components/JobMapSection';
import { REQUEST_ACCESS_PATH, CONTRIBUTOR_PATH } from './constants';

/* The homepage — the design lab's "data product" direction promoted to /.
   Plain CSS (homepage.css), dark-only, no MUI: the lab's conclusion was that
   the marketing surface drops MUI while the logged-in app keeps it. All copy
   renders from ./content.js — edit there, not here. */

const NAV_SECTIONS = [
  { id: 'research', label: 'The research' },
  { id: 'job-map', label: 'Job maps' },
  { id: 'how-it-works', label: 'How it works' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'faq', label: 'FAQ' },
];

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function Nav() {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  const go = (id) => () => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header className="dp-nav">
      <Link to="/" className="dp-brand">
        Conversation Intelligence
      </Link>
      <nav className="dp-nav-links">
        {NAV_SECTIONS.map((s) => (
          <button key={s.id} type="button" className="dp-nav-link" onClick={go(s.id)}>
            {s.label}
          </button>
        ))}
      </nav>
      <div className="dp-nav-cta">
        <Link className="dp-nav-link" to={CONTRIBUTOR_PATH}>
          For contributors
        </Link>
        <Link className="dp-nav-link" to="/login">
          Log in
        </Link>
        <button
          type="button"
          className="dp-btn dp-btn-primary dp-btn-sm"
          onClick={() => navigate(REQUEST_ACCESS_PATH)}
        >
          Request access
        </button>
      </div>
      <button
        type="button"
        className="dp-nav-burger"
        aria-label="Menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        ≡
      </button>
      <div className={open ? 'dp-nav-drawer dp-nav-drawer-open' : 'dp-nav-drawer'}>
        {NAV_SECTIONS.map((s) => (
          <button key={s.id} type="button" className="dp-nav-link" onClick={go(s.id)}>
            {s.label}
          </button>
        ))}
        <Link className="dp-nav-link" to={CONTRIBUTOR_PATH}>
          For contributors
        </Link>
        <Link className="dp-nav-link" to="/login">
          Log in
        </Link>
      </div>
    </header>
  );
}

function Hero() {
  const navigate = useNavigate();
  return (
    <section className="dp-hero" id="hero">
      <div className="dp-shell">
        <span className="dp-tag">customer research library</span>
        <h1>{meta.headline}</h1>
        <p className="dp-lede">{meta.sub}</p>
        <div className="dp-actions">
          <button
            type="button"
            className="dp-btn dp-btn-primary"
            onClick={() => navigate(REQUEST_ACCESS_PATH)}
          >
            {meta.primaryCta}
          </button>
          <button type="button" className="dp-btn" onClick={() => scrollTo('research')}>
            {meta.secondaryCta}
          </button>
        </div>
        <p className="dp-audience">{meta.audience}</p>
        <div className="dp-stats">
          {meta.stats.map((s) => (
            <div key={s.label}>
              <strong>{s.value}</strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Approach() {
  return (
    <section className="dp-section" id="approach">
      <div className="dp-shell">
        <h2>{approach.title}</h2>
        <p className="dp-body">{approach.intro}</p>
        <div className="dp-grid-3">
          {approach.items.map((item) => (
            <div key={item.title} className="dp-card">
              <h4>{item.title}</h4>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Connector() {
  return (
    <section className="dp-section dp-connector" id="connector">
      <div className="dp-shell">
        <span className="dp-tag">{connector.kicker}</span>
        <h2 className="dp-connector-title">
          <ClaudeMark />
          {connector.title}
        </h2>
        <p className="dp-body">{connector.intro}</p>
        <p className="dp-body">{connector.detail}</p>
        <div className="dp-terminal">
          {connector.examples.map((example) => (
            <div key={example} className="dp-term-line">
              <span className="dp-term-caret">&gt;</span>
              <span>{example}</span>
            </div>
          ))}
        </div>
        <p className="dp-legend">{connector.footnote}</p>
      </div>
    </section>
  );
}

function Problem() {
  return (
    <section className="dp-section" id="problem">
      <div className="dp-shell">
        <h2>{problem.title}</h2>
        <div className="dp-grid-2">
          {problem.quotes.map((q) => (
            <div key={q} className="dp-card dp-card-quote">
              <p>“{q}”</p>
            </div>
          ))}
        </div>
        <h4 className="dp-subhead">{problem.alternativesTitle}</h4>
        {problem.alternatives.map((a) => (
          <div key={a.option} className="dp-alt-row">
            <span className="dp-alt-name">{a.option}</span>
            <span className="dp-alt-gap">{a.gap}</span>
          </div>
        ))}
        <p className="dp-body dp-closer">{problem.closer}</p>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="dp-section" id="how-it-works">
      <div className="dp-shell">
        <h2>{howItWorks.title}</h2>
        <p className="dp-body">{howItWorks.intro}</p>
        <div className="dp-grid-4">
          {howItWorks.steps.map((s, i) => (
            <div key={s.title} className="dp-card">
              <span className="dp-step-n">{String(i + 1).padStart(2, '0')}</span>
              <h4>{s.title}</h4>
              <p>{s.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatYouGet() {
  return (
    <section className="dp-section" id="what-you-get">
      <div className="dp-shell">
        <h2>{whatYouGet.title}</h2>
        <p className="dp-body">{whatYouGet.intro}</p>
        {whatYouGet.groups.map((group) => (
          <div key={group.heading}>
            <h4 className="dp-subhead">{group.heading}</h4>
            <div className="dp-grid-3">
              {group.items.map((item) => (
                <div key={item.title} className="dp-card">
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function BringYourOwn() {
  const navigate = useNavigate();
  return (
    <section className="dp-section" id="bring-your-own">
      <div className="dp-shell">
        <h2>{bringYourOwn.title}</h2>
        <p className="dp-body">{bringYourOwn.body}</p>
        <button type="button" className="dp-btn" onClick={() => navigate(REQUEST_ACCESS_PATH)}>
          {bringYourOwn.cta}
        </button>
      </div>
    </section>
  );
}

function Pricing() {
  const navigate = useNavigate();
  return (
    <section className="dp-section" id="pricing">
      <div className="dp-shell">
        <h2>{pricing.title}</h2>
        <p className="dp-body">{pricing.intro}</p>
        <div className="dp-grid-2">
          {pricing.tiers.map((t) => (
            <div key={t.name} className={t.featured ? 'dp-tier dp-tier-featured' : 'dp-tier'}>
              <h4>{t.name}</h4>
              <p className="dp-price">{t.price}</p>
              <span className="dp-period">{t.period}</span>
              <span className="dp-tier-sub">{t.sub}</span>
              <ul>
                {t.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              <button
                type="button"
                className="dp-btn dp-btn-primary"
                onClick={() => navigate(REQUEST_ACCESS_PATH)}
              >
                {pricing.cta}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section className="dp-section" id="faq">
      <div className="dp-shell">
        <h2>{faq.title}</h2>
        {faq.items.map((item) => (
          <div key={item.q} className="dp-faq">
            <h4>{item.q}</h4>
            <p>{item.a}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="dp-footer">
      <div className="dp-shell">
        <div className="dp-footer-grid">
          <div className="dp-footer-tagline">{footer.tagline}</div>
          <div className="dp-footer-col">
            <h5>Product</h5>
            {NAV_SECTIONS.map((s) => (
              <button key={s.id} type="button" onClick={() => scrollTo(s.id)}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="dp-footer-col">
            <h5>Company</h5>
            <Link to="/contact">Contact</Link>
            <Link to="/login">Log in</Link>
          </div>
          <div className="dp-footer-col">
            <h5>Contribute</h5>
            <Link to={CONTRIBUTOR_PATH}>Get paid to share your workflow</Link>
          </div>
          <div className="dp-footer-col">
            <h5>Legal</h5>
            <Link to="/privacy">Privacy policy</Link>
            <Link to="/terms">Terms</Link>
          </div>
        </div>
        <div className="dp-footer-base">
          <span>© {new Date().getFullYear()} Conversation Intelligence</span>
          <a href={`mailto:${footer.email}`}>{footer.email}</a>
        </div>
      </div>
    </footer>
  );
}

export default function MarketingPage() {
  return (
    <div className="dp">
      <Nav />
      <Hero />
      <PromoVideo />
      <Approach />
      <Connector />
      <StorySection />
      <JobMapSection />
      <Problem />
      <HowItWorks />
      <WhatYouGet />
      <BringYourOwn />
      <Pricing />
      <Faq />
      <Footer />
    </div>
  );
}
